// src/shared/components/TagInput.tsx

import { useEffect, useState } from 'react';
import { TagsInput, type ComboboxItem, type OptionsFilter } from '@mantine/core';
import { TAG_CONSTRAINTS, TAG_SPLIT_CHARS } from '@/lib/constants/tag';
import { TagInputUtils } from './TagInputUtils';

/**
 * Prefix-based filter for tag suggestions.
 * Only shows tags that start with the typed text (case-insensitive).
 * More predictable than the default substring matching for tag selection.
 */
const prefixFilter: OptionsFilter = ({ options, search }) => {
  const query = search.toLowerCase().trim();
  if (!query) return options;
  return (options as ComboboxItem[]).filter((option) =>
    option.label.toLowerCase().startsWith(query)
  );
};

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  onBlur?: () => void;
  allTags: string[];
  allowNewTags?: boolean;
  /**
   * Surface-specific guidance appended to the "doesn't match an available
   * tag" rejection message (only relevant when allowNewTags is false).
   */
  notFoundHint?: string;
  maxTags?: number;
  error?: string;
  disabled?: boolean;
  label?: string;
  description?: string;
  placeholder?: string;
}

export function TagInput({
  value,
  onChange,
  onBlur,
  allTags,
  allowNewTags = true,
  notFoundHint,
  maxTags = TAG_CONSTRAINTS.MAX_TAGS_PER_MEDIA,
  error,
  disabled = false,
  label = 'Tags',
  description,
  placeholder = 'Add a tag...',
}: TagInputProps) {
  // Suggestions only while the user is typing (prefix filter is
  // typing-oriented anyway). Committing a tag clears the search, so the
  // dropdown closes instead of lingering over the content below the field —
  // an open dropdown there swallows clicks meant for buttons under it.
  const [searchValue, setSearchValue] = useState('');

  // Feedback when a typed value is dropped because it isn't an existing tag
  // (allowNewTags=false) — without it the text just vanishes, which users
  // read as "tags aren't saving".
  const [rejectionMessage, setRejectionMessage] = useState<string | null>(null);

  // Mantine clears the search in the same event as the commit — before the
  // value change on Enter, after it on split-char/paste — so restoring the
  // rejected text inline would be clobbered on one of those paths. An effect
  // runs after the whole batch, winning regardless of internal call order.
  const [pendingRestore, setPendingRestore] = useState<string | null>(null);

  useEffect(() => {
    if (pendingRestore !== null) {
      setSearchValue(pendingRestore);
      setPendingRestore(null);
    }
  }, [pendingRestore]);

  function handleChange(newTags: string[]): void {
    const { accepted, rejected } = TagInputUtils.processChange({
      newTags,
      allTags,
      allowNewTags,
      previousValue: value,
    });

    setRejectionMessage(TagInputUtils.formatRejection(rejected, notFoundHint));
    // Keep the rejected text in the field so it doesn't silently vanish.
    // Single value only — a multi-value paste just gets the message.
    if (rejected.length === 1) setPendingRestore(rejected[0]);

    onChange(accepted);
  }

  function handleSearchChange(next: string): void {
    // Typing again dismisses the rejection feedback. Mantine's automatic
    // clear-after-commit ('') must not, or the message would never be seen.
    if (next !== '') setRejectionMessage(null);
    setSearchValue(next);
  }

  const tagCount = `${value.length}/${maxTags} tags`;

  return (
    <TagsInput
      label={label}
      description={description ?? tagCount}
      placeholder={value.length >= maxTags ? undefined : placeholder}
      value={value}
      onChange={handleChange}
      onBlur={onBlur}
      searchValue={searchValue}
      onSearchChange={handleSearchChange}
      dropdownOpened={searchValue.trim().length > 0}
      data={allTags}
      maxTags={maxTags}
      splitChars={[...TAG_SPLIT_CHARS]}
      allowDuplicates={false}
      acceptValueOnBlur={false}
      filter={prefixFilter}
      limit={TAG_CONSTRAINTS.MAX_SUGGESTIONS_SHOWN}
      styles={{
        pill: {
          backgroundColor: 'var(--mantine-primary-color-light)',
          color: 'var(--mantine-primary-color-filled)',
        },
      }}
      error={error ?? rejectionMessage}
      disabled={disabled}
    />
  );
}
